import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Shown once per user when the current Terms version hasn't been accepted.
// Lives behind a Dashboard mount so it only fires after auth and household
// onboarding — never blocks login itself, never appears on /terms reading.
export default function TermsGate() {
  const { user } = useAuth();
  const [needs, setNeeds] = useState(false);
  const [version, setVersion] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) return;
      try {
        const r = await api.get("/terms/me");
        if (cancelled) return;
        if (!r.data?.accepted) {
          setNeeds(true);
          setVersion(r.data?.current_version || null);
        }
      } catch (_) { /* silent — non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const accept = async () => {
    try {
      setBusy(true);
      await api.post("/terms/accept", { version });
      setNeeds(false);
    } catch (_) {
      // Even on failure we close — they'll be re-prompted next session.
      setNeeds(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={needs} onOpenChange={() => {/* gated — only Accept closes */}}>
      <DialogContent
        data-testid="terms-gate"
        className="bg-chalk border-2 border-stone_border max-w-sm"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-timber-900">
            One quick agreement
          </DialogTitle>
          <DialogDescription className="text-timber-700 text-sm leading-relaxed pt-2">
            Half Job is a planning tool — not a substitute for a licensed tradie.
            By continuing you agree to our{" "}
            <a href="/terms" target="_blank" rel="noreferrer" data-testid="terms-gate-link" className="underline font-bold">
              Terms &amp; Acceptable Use
            </a>{" "}
            — no illegal work, no jailbreak attempts, just home DIY.
          </DialogDescription>
        </DialogHeader>
        <Button
          data-testid="terms-gate-accept"
          onClick={accept}
          disabled={busy}
          className="w-full bg-timber-900 text-masking hover:bg-timber-900 min-h-[52px] font-bold mt-3"
        >
          {busy ? "Saving…" : "I agree — let's go"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
